import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  Backdrop,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PaymentUploadDialog from './PaymentUploadDialog';
import { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';
import { ILegalTransactionGwGEntry } from '../../../Interfaces/ILegalTransactionGwGEntry';
import { customFetcher, useFetch } from '../../../hooks/useFetch';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { IContact } from '../../../Interfaces/IContact';
import { Loader } from '../../generic/Loader';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface PaymentTabProps {
  legalTransaction: {
    LegalTransactionGwGArray: ILegalTransactionGwGFullObject[];
  };
  idLegalTransaction: number;
  idContact: number;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// mapping (im Notafall)
const mimeToId = (mime: string): number => {
  switch (mime) {
    case 'application/pdf':
      return 4;
    case 'image/jpeg':
    case 'image/jpg':
      return 5;
    case 'image/png':
      return 6;
    default:
      return 4;
  }
};

const PaymentTab: React.FC<PaymentTabProps> = ({
  legalTransaction,
  idContact,
  idLegalTransaction,
}) => {
  const [clientEdit] =
    useFetch<IContact>('/contact');
  const [gwgArray, setGwgArray] = useState<ILegalTransactionGwGFullObject[]>(
    legalTransaction.LegalTransactionGwGArray || []
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [parentIdForNew, setParentIdForNew] = useState<number | null>(null);
  const [addNewPaymentName, setAddNewPaymentName] = useState<string >("");

  const [attachTarget, setAttachTarget] = useState<{
    gwgId: number;
    entry: ILegalTransactionGwGEntry;
  } | null>(null);

  // Dialog zum Anzeigen der Dokument-Namen
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docDialogEntry, setDocDialogEntry] =
    useState<ILegalTransactionGwGEntry | null>(null);
  const openDocsDialog = (entry: ILegalTransactionGwGEntry) => {
    setDocDialogEntry(entry);
    setDocDialogOpen(true);
  };
  const [isUploading, setIsUploading] = useState(false);

  const [feedback, setFeedback] = useState<{
    open: boolean;
    title: string;
    msg: string;
    severity: 'success' | 'error';
  }>({ open: false, title: '', msg: '', severity: 'success' });

  const handleAddPayment = (gwGFullObject:  ILegalTransactionGwGFullObject) => {
    setAttachTarget(null);
    const id = gwGFullObject.idLegalTransactionGwG;

    setParentIdForNew(id);
    const now = new Date();
    const formatted = now.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) .replace(', ', '-');
    setAddNewPaymentName(`Zahlung-${clientEdit?.FirstName}${clientEdit?.LastName}-${formatted}`);

    setDialogOpen(true);
  };

  const handleAttachDocument = (
    gwgId: number,
    entry: ILegalTransactionGwGEntry
  ) => {
    setParentIdForNew(null);
    setAttachTarget({ gwgId, entry });
    setDialogOpen(true);
  };

  const getStateChip = (state?: number | null) => {
    const normalized = state ?? 1;

    const label =
      normalized === 1 ? 'Prüfung' : normalized === 2 ? 'Geprüft' : 'Abgelehnt';

    const color =
      normalized === 1 ? 'warning' : normalized === 2 ? 'success' : 'error';

    return { label, color };
  };

  const getSumWasPaid = (gwgEntryArray: ILegalTransactionGwGEntry[]) => {
    const paidSum = gwgEntryArray
      .filter(x => x.wasPaid && x.idLegalTransactionGwGEntryState === 2)
      .map(x => x.currentValue)
      .reduce((a, b) => a + b, 0);
    return paidSum.toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const getDiffPaid = (gwgEntryArray: ILegalTransactionGwGEntry[], gwg: ILegalTransactionGwGFullObject) => {
    const paidSum = gwgEntryArray
      .filter(x => x.isPaid)
      .map(x => x.currentValue)
      .reduce((a, b) => a + b, 0);
    const wasPaidSum = gwgEntryArray
      .filter(x => !x.isPaid)
      .filter(x => x.wasPaid)
      .map(x => x.currentValue + (x.currentRate ?? 0) + (x.extraCosts ?? 0))
      .reduce((a, b) => a + b, 0);
    return (gwg.total - paidSum - wasPaidSum).toLocaleString(
      'de-DE',
      { style: 'currency', currency: 'EUR' }
    );
  };

  const handleSaveDialog = async (
    file: File | null,
    meta: { title: string; amount: number }
  ) => {
    const isAttach = !!attachTarget;
    const gwgId = isAttach ? attachTarget!.gwgId : parentIdForNew;

    if (!gwgId) {
      setFeedback({
        open: true,
        title: 'Fehler',
        msg: 'GWG-Ziel fehlt.',
        severity: 'error',
      });
      return;
    }

    if (!file) {
      setFeedback({
        open: true,
        title: 'Hinweis',
        msg: 'Bitte eine Datei hochladen.',
        severity: 'error',
      });
      return;
    }

    setIsUploading(true);

    try {
      const base64 = await fileToBase64(file);

      const payload = {
        idContact,
        idLegalTransaction,
        Title: meta.title,
        Amount: meta.amount,
        idLegalTransactionGwG: gwgId,
        idLegalTransactionGwGEntry: isAttach
          ? attachTarget!.entry.idLegalTransactionGwGEntry
          : -1,
        Document: {
          Data: base64,
          FileName: file.name,
          idMIMEType: mimeToId(file.type),
        },
      };

      const res: { ok: boolean } = await customFetcher({
        endpoint: '/payment',
        method: 'POST',
        body: payload,
      });

      if (!res || !res.ok) {
        throw new Error('Upload fehlgeschlagen');
      }


      // zeigt temporär die hochgeladenen Dokumente an (nicht nach tabwechsel)
      /*setGwgArray(gwgArray.map(gwg => gwg.idLegalTransactionGwG === attachTarget?.gwgId
        ?  {
          ...gwg,
          LegalTransactionGwGEntryArray: gwg.LegalTransactionGwGEntryArray.map(entry =>
            entry.idLegalTransactionGwGEntry === attachTarget.entry.idLegalTransactionGwGEntry
              ? {
                ...entry,
                DocumentArray: [
                  ...(entry.DocumentArray || []),
                  {
                    idDocument: -1, // temporäre Dummy-ID
                    FileName: file.name,
                    idMIMEType: mimeToId(file.type),
                  },
                ],
              }
              : entry
          ),
        }  : gwg ));*/

      // Dialog schließen
      setDialogOpen(false);
      setParentIdForNew(null);
      setAttachTarget(null);

      // Bestätigungsdialog
      setFeedback({
        open: true,
        title: 'Dokument hochgeladen',
        msg: 'Ihre Einzahlung wurde erfolgreich an das Notariat übermittelt und wird geprüft.\n' +
          'Nach Abschluss der Prüfung wird das Dokument für Sie sichtbar sein.',
        severity: 'success',
      });
    } catch (e: any) {
      console.error('Fehler beim Speichern:', e);
      setFeedback({
        open: true,
        title: 'Dokument hochladen fehlgeschlagen',
        msg: e?.message || 'Upload fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (!clientEdit) {
    return <Loader />;
  }

  return (
    <Box>
      {gwgArray.map(gwg => (
        <Box
          key={gwg.idLegalTransactionGwG}
          sx={{
            mb: 3,
            p: 2,
            border: '1px solid #000',
            borderRadius: 2,
            backgroundColor: '#fff',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            {gwg.title}
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item sm={12}>
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Gesamtsumme / Kaufpreis
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {gwg.total.toLocaleString('de-DE', {
                    style: 'currency',
                    currency: 'EUR',
                    currencyDisplay: 'code',
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid item sm={12}>
              <Typography

              >
                Gezahlt: {getSumWasPaid(gwg.LegalTransactionGwGEntryArray)} von{' '}
                {gwg.total.toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                })}
                <br />
                übrig: {getDiffPaid(gwg.LegalTransactionGwGEntryArray, gwg)}
              </Typography>
            </Grid>
            <Grid item sm={12}>
              <Button
                variant='outlined'
                onClick={() => handleAddPayment(gwg)}
                sx={{ mt: 1 }}
              >
                Neue Zahlung hinzufügen
              </Button>
            </Grid>

            {/* Bisherige Zahlungen */}
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle1'>Bisherige Zahlungen</Typography>
              {gwg.LegalTransactionGwGEntryArray.filter(
                e => e.isPaid
              ).map(entry => {
                const { label, color } = getStateChip(
                  entry.idLegalTransactionGwGEntryState
                );
                const docCount = entry.DocumentArray?.length ?? 0;

                return (
                  <Box
                    key={entry.idLegalTransactionGwGEntry}
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1.25,
                      mb: 1.25,
                    }}
                  >
                    {/* Status oben rechts */}
                    <Chip
                      label={label}
                      size='small'
                      variant='outlined'
                      color={color as 'warning' | 'success' | 'error'}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />

                    {/* Zeile 1: Titel */}
                    <Typography variant='subtitle2' sx={{ pr: 10 }} noWrap>
                      {entry.title}
                    </Typography>

                    {/* Zeile 2: Betrag + Doku-Chip + Upload */}
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                      <Typography variant='body2'>
                        Betrag: {entry.currentValue.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        currencyDisplay: 'narrowSymbol',
                      })}
                      </Typography>


                      <Tooltip title='Dokumente anzeigen'>
                        <span>
                          <Chip
                            icon={<InsertDriveFileIcon fontSize='small' />}
                            label={docCount}
                            size='small'
                            variant='outlined'
                            clickable
                            onClick={() => openDocsDialog(entry)}
                          />
                        </span>
                      </Tooltip>

                      <Tooltip title='Dokument hinzufügen'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleAttachDocument(
                                gwg.idLegalTransactionGwG,
                                entry
                              )
                            }
                            aria-label='Dokument hinzufügen'
                          >
                            <FileUploadIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Grid>

            {/* Bisherige Ablöseforderungen */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                Bisherige Ablöseforderungen
                <Tooltip
                  title="Ablöseforderungen sind bestehende finanzielle Verpflichtungen, die nicht direkt an den Verkäufer fließen, sondern zur Begleichung offener Positionen gegenüber Dritten bestimmt sind."
                  arrow
                >
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              {gwg.LegalTransactionGwGEntryArray.filter(
                e => !e.isPaid
              ).map(entry => {
                const docCount = entry.DocumentArray?.length ?? 0;

                return (
                  <Box
                    key={entry.idLegalTransactionGwGEntry}
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1.25,
                      mb: 1.25,
                    }}
                  >
                    {/* Zeile 1: Titel */}
                    <Typography variant='subtitle2' noWrap>
                      {entry.title}
                    </Typography>

                    {/* Zeile 2: Betrag + Doku-Chip + Upload */}
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                      <Typography variant='body2'>
                        Betrag: {entry.currentValue} €
                      </Typography>
                      <Typography variant='body2'>
                        Tagessatz: {entry.currentRate} €
                      </Typography>
                      <Typography variant='body2'>
                        Nebenkosten: {entry.extraCosts} €
                      </Typography>

                      <Tooltip title='Dokumente anzeigen'>
                        <span>
                          <Chip
                            icon={<InsertDriveFileIcon fontSize='small' />}
                            label={docCount}
                            size='small'
                            variant='outlined'
                            clickable
                            onClick={() => openDocsDialog(entry)}
                          />
                        </span>
                      </Tooltip>

                      <Tooltip title='Dokument hinzufügen'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleAttachDocument(
                                gwg.idLegalTransactionGwG,
                                entry
                              )
                            }
                            aria-label='Dokument hinzufügen'
                          >
                            <FileUploadIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </Box>
      ))}

      {dialogOpen && (
        <PaymentUploadDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setParentIdForNew(null);
            setAttachTarget(null);
          }}
          onSave={handleSaveDialog}
          requireFile
          initialTitle={attachTarget?.entry.title ?? addNewPaymentName}
          initialAmount={attachTarget?.entry.currentValue ?? undefined}
          lockMetaFields={!!attachTarget}
          hideTitle={!attachTarget}
          dialogTitle={
            attachTarget
              ? 'Weiteres Dokument hinzufügen'
              : `Neue Zahlung hinzufügen`
          }
        />
      )}

      {/* Dialog: Dokumentnamen anzeigen */}
      <Dialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Dokumente: {docDialogEntry?.title ?? ''}</DialogTitle>
        <DialogContent dividers>
          {!docDialogEntry?.DocumentArray?.length ? (
            <Typography variant='body2' color='text.secondary'>
              Keine Dokumente vorhanden.
            </Typography>
          ) : (
            <List dense>
              {docDialogEntry!.DocumentArray!.map(doc => (
                <ListItem key={doc.idDocument}>
                  <ListItemIcon>
                    <InsertDriveFileIcon fontSize='small' />
                  </ListItemIcon>

                  <ListItemText
                    primary={doc.FileName}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          mt: 0.25,
                          opacity: 0.75,
                        }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        <Typography variant='caption' color='text.secondary'>
                          Hochgeladen von:{' '}
                          {typeof doc.uploaderName === 'string' &&
                          doc.uploaderName.trim() !== ''
                            ? doc.uploaderName.trim()
                            : 'Notariat'}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDocDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Upload-Backdrop */}
      <Backdrop
        open={isUploading}
        sx={{ color: '#fff', zIndex: t => t.zIndex.modal + 2 }}
      >
        <CircularProgress />
      </Backdrop>

      {/* Feedback-Dialog*/}
      <Dialog
        open={feedback.open}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{feedback.title}</DialogTitle>
        <DialogContent dividers>
          <Alert severity={feedback.severity}  sx={{ whiteSpace: 'pre-line' }}>{feedback.msg}</Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFeedback(f => ({ ...f, open: false }))}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentTab;
